import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useTasksRealtime } from './useTasksRealtime';
import { useAuth } from './useAuth';
import { useTenantId } from './useTenantId';

export interface Task {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  status: 'not_started' | 'in_progress' | 'stuck' | 'done' | 'archived';
  priority: 'critical' | 'high' | 'medium' | 'low';
  assigned_to_contact_id: string | null;
  assigned_to_name: string | null;
  assigned_to_email: string | null;
  project_id: string | null;
  due_date: string | null;
  due_time: string | null;
  start_date: string | null;
  completed_at: string | null;
  urgency_score: number | null;
  sentiment: string | null;
  confidence_score: number | null;
  task_signature: string | null;
  duplicate_check_hash: string | null;
  tags: string[] | null;
  estimated_hours: number | null;
  actual_hours: number | null;
  created_at: string;
  updated_at: string;
  created_by_ai: boolean;
  last_email_received_at: string | null;
  linked_emails_count?: number;
  project?: {
    id: string;
    name: string;
    status: string;
  };
}

export function useTasks() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { tenantId } = useTenantId();
  
  // Subscribe to real-time updates
  useTasksRealtime(user?.id);

  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['tasks', tenantId],
    queryFn: async () => {
      if (!user || !tenantId) return [];

      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          linked_emails_count:task_email_links(count),
          project:projects(id, name, status)
        `)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(task => ({
        ...task,
        linked_emails_count: task.linked_emails_count?.[0]?.count || 0,
        project: task.project || null,
      })) as Task[];
    },
    enabled: !!user && !!tenantId,
  });

  // Ensure we always return an array, even if cache contains null
  const tasks = tasksData ?? [];

  const createTask = useMutation({
    mutationFn: async (newTask: Partial<Task>) => {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...newTask,
          tenant_id: tenantId,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', tenantId] });
      toast({
        title: 'Task created',
        description: 'Your task has been created successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error creating task',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateTask = useMutation({
    mutationFn: async ({ taskId, ...updates }: Partial<Task> & { taskId: string }) => {
      const { data, error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', taskId)
        .eq('tenant_id', tenantId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ taskId, ...updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', tenantId] });
      
      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData<Task[]>(['tasks', tenantId]);
      
      // Optimistically update to the new value
      queryClient.setQueryData<Task[]>(['tasks', tenantId], (old) => {
        if (!old) return old;
        return old.map(task => 
          task.id === taskId 
            ? { ...task, ...updates, updated_at: new Date().toISOString() }
            : task
        );
      });
      
      // Return context with snapshot
      return { previousTasks };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', tenantId] });
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', tenantId], context.previousTasks);
      }
      toast({
        title: 'Error updating task',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteTask = useMutation({
    mutationFn: async (taskId: string) => {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId)
        .eq('tenant_id', tenantId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', tenantId] });
      toast({
        title: 'Task deleted',
        description: 'Your task has been deleted successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error deleting task',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    tasks,
    isLoading,
    createTask,
    updateTask,
    deleteTask,
  };
}
